import type { DashboardData, LeaderboardPlayer, LiveGameData, Question, QuestionDetail, QuestionPage, QuestionUpdateInput } from "./types";

export type WorkspaceSettings = {
  brand: { appName: string; botName: string; description: string };
  telegram: { botUsername: string; groupStartEnabled: boolean };
  game: { duration: number; difficulty: "easy" | "normal" | "hard"; wrongAnswerMode: "silent" | "short_reply" | "temporary_reply"; autoReveal: boolean; autoNext: boolean; memberStart: boolean; basePoints: number };
  schedule: { timezone: string; autoNextDelay: number; scheduleEnabled: boolean };
  security: { adminOnlyConnect: boolean; requireReply: boolean; maxAttempts: number; cooldownSeconds: number };
  media: { maxUploadMb: number; keepOriginal: boolean; privateMedia: boolean };
};
export type SettingsResponse = { settings: WorkspaceSettings; secrets: { botTokenConfigured: boolean; webhookSecretConfigured: boolean } };
export type AuthUser = { id: string; displayName: string; role: string; csrfToken: string; permissions: string[] };
export type AdminAccount = { id: string; displayName: string; email: string | null; role: string; status: string; lastLoginAt: string | null; createdAt: string };
export type GameCategory = { id: string; name: string; slug: string; enabled: boolean; totalQuestions: number; publishedQuestions: number };

type Envelope<T> = { data: T; requestId?: string };
let csrfToken: string | null = null;

async function authContext() {
  const response = await fetch("/api/auth/me", { credentials: "include" });
  const payload = await response.json() as Envelope<AuthUser> & { error?: string };
  if (!response.ok) throw new ApiError(payload.error || "Sesi admin diperlukan.", response.status);
  csrfToken = payload.data.csrfToken;
  return payload.data;
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const mutation = init?.method && !["GET", "HEAD", "OPTIONS"].includes(init.method.toUpperCase());
  if (mutation && !csrfToken) await authContext();
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(mutation ? { "X-CSRF-Token": csrfToken || "" } : {}), ...init?.headers },
  });
  const payload = (await response.json().catch(() => null)) as Envelope<T> | { error?: string } | null;
  if (!response.ok) {
    throw new ApiError((payload && "error" in payload && payload.error) || "Permintaan gagal diproses.", response.status);
  }
  return (payload as Envelope<T>).data;
}

export const api = {
  emailLogin: async (email: string, password: string) => {
    const response = await fetch("/auth/email", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const payload = await response.json().catch(() => null) as Envelope<{ ok: boolean }> & { error?: string } | null;
    if (!response.ok) throw new ApiError(payload?.error || "Login belum dapat diproses.", response.status);
    return payload!.data;
  },
  me: authContext,
  admins: () => request<AdminAccount[]>("/api/admins"),
  createAdmin: (input: { displayName: string; email: string; password: string; role?: "MASTER" | "QUESTION_CREATOR" | "CONTENT_PUBLISHER" }) =>
    request<AdminAccount>("/api/admins", { method: "POST", body: JSON.stringify({ ...input, role: input.role || "CONTENT_PUBLISHER" }) }),
  dashboard: () => request<DashboardData>("/api/dashboard"),
  questions: (search = "", status = "all", difficulty = "all", page = 1, pageSize = 100) =>
    request<QuestionPage>(`/api/questions?search=${encodeURIComponent(search)}&status=${encodeURIComponent(status)}&difficulty=${encodeURIComponent(difficulty)}&page=${page}&pageSize=${pageSize}`),
  question: (id: string) => request<QuestionDetail>(`/api/questions/${encodeURIComponent(id)}`),
  updateQuestion: (id: string, input: QuestionUpdateInput) => request<QuestionDetail>(`/api/questions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteQuestion: (id: string) => request<{ ok: boolean }>(`/api/questions/${encodeURIComponent(id)}`, { method: "DELETE" }),
  liveGame: () => request<LiveGameData | null>("/api/games/live"),
  startGame: (chatId: string, questionId?: string) => request<{ gameId: string }>("/api/games/start", { method: "POST", body: JSON.stringify({ chatId, questionId }) }),
  gameAction: (action: string, gameId: string) =>
    request<{ ok: boolean }>(`/api/games/${encodeURIComponent(gameId)}/${action}`, { method: "POST", body: "{}" }),
  uploadQuestion: async (form: FormData) => {
    if (!csrfToken) await authContext();
    const response = await fetch("/api/questions", { method: "POST", body: form, credentials: "include", headers: { "X-CSRF-Token": csrfToken || "" } });
    const payload = (await response.json()) as Envelope<{ id: string }> & { error?: string };
    if (!response.ok) throw new ApiError(payload.error || "Gagal menyimpan soal.", response.status);
    return payload.data;
  },
  sendTargets: () => request<Array<{ chatId: string; title: string }>>("/api/send-targets"),
  sendQuestion: (questionId: string, chatId: string) => request<{ gameId: string }>(`/api/questions/${encodeURIComponent(questionId)}/send`, { method: "POST", body: JSON.stringify({ chatId }) }),
  updateQuestionStatus: (id: string, status: "draft" | "published" | "disabled" | "archived") => request<{ ok: boolean }>(`/api/questions/${encodeURIComponent(id)}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  duplicateQuestion: (id: string) => request<{ id: string }>(`/api/questions/${encodeURIComponent(id)}/duplicate`, { method: "POST", body: "{}" }),
  groups: () => request<Array<Record<string, string | number | boolean | null>>>("/api/groups"),
  createConnectCode: () => request<{ code: string; expiresInSeconds: number }>("/api/groups/connect-code", { method: "POST", body: "{}" }),
  updateGroupSettings: (id: string, settings: { gameDurationSeconds: number; allowMemberStart: boolean; scheduleEnabled: boolean; autoRevealTimeout: boolean }) => request<{ ok: boolean }>(`/api/groups/${encodeURIComponent(id)}/settings`, { method: "PATCH", body: JSON.stringify(settings) }),
  resetGroupBot: (id: string, resetDeck: boolean) => request<{ ok: boolean; clearedGames: number; roomsCleared: number; deckReset: boolean }>(`/api/groups/${encodeURIComponent(id)}/reset`, { method: "POST", body: JSON.stringify({ resetDeck }) }),
  settings: () => request<SettingsResponse>("/api/settings"),
  saveSettings: (settings: WorkspaceSettings) => request<SettingsResponse>("/api/settings", { method: "PATCH", body: JSON.stringify(settings) }),
  categories: () => request<GameCategory[]>(`/api/categories?_=${Date.now()}`),
  updateCategory: (id: string, enabled: boolean) => request<{ ok: boolean }>(`/api/categories/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ enabled }) }),
  leaderboard: () => request<LeaderboardPlayer[]>("/api/leaderboard"),
  updateLeaderboardScore: (telegramUserId: string, score: number, reason: string) => request<{ ok: boolean; previousScore: number; score: number; delta: number }>(`/api/leaderboard/${encodeURIComponent(telegramUserId)}/score`, { method: "PATCH", body: JSON.stringify({ score, reason }) }),
  audit: () => request<Array<Record<string, string | null>>>("/api/audit"),
};
